import { useState } from "react";
import { toast } from "sonner";
import { Vote, Coins, Trophy, Plus, Play, Square, Download, ChevronDown, ChevronRight, Check, Banknote, Loader2 } from "lucide-react";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Field, Btn, DeleteBtn, Loading, Empty, useCrud, handleErr } from "./AdminUI";
import { ImageUpload } from "@/components/ImageUpload";

const Select = ({ label, value, onChange, children }) => (
  <label className="block"><span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
    <select value={value} onChange={onChange} className="mt-1.5 w-full h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm">{children}</select></label>
);
const toLocalInput = (iso) => (iso ? String(iso).slice(0, 16) : "");
const toIso = (v) => (v ? new Date(v).toISOString() : null);
const BLANK = { title: "", description: "", type: "free", pointsPerVote: 1, isReward: false, coverUrl: "", startAt: "", closeAt: "", resultsPublic: true, allowSignups: false };

const PAYOUT_BADGE = { pending: "bg-white/5 text-slate-400", approved: "bg-amber-500/15 text-amber-400", processing: "bg-blue-500/15 text-blue-300", paid: "bg-emerald-500/15 text-emerald-400", failed: "bg-red-500/15 text-red-400" };

function Options({ c, reload }) {
  const [opt, setOpt] = useState({ name: "", description: "", imageUrl: "", payoutPhone: "" });
  const [busy, setBusy] = useState(null);

  const add = async () => {
    if (!opt.name.trim()) { toast.error("Participant/option name required"); return; }
    try { await endpoints.adminAddOption(c.id, opt); toast.success("Option added"); setOpt({ name: "", description: "", imageUrl: "", payoutPhone: "" }); reload(); }
    catch (e) { handleErr(e); }
  };
  const del = async (id) => { if (!window.confirm("Delete this option?")) return; try { await endpoints.adminDeleteOption(id); reload(); } catch (e) { handleErr(e); } };
  const approve = async (o) => {
    const amt = window.prompt(`Reward amount to send to ${o.name} (RWF):`, o.amountFrw || o.rewardAmountFrw || "");
    if (amt === null) return;
    setBusy(o.id);
    try {
      const { data } = await endpoints.adminApproveReward(o.id, { amountFrw: parseInt(amt || "0", 10) });
      if (data.payoutStatus === "paid") toast.success(data.dev ? "Payout recorded (dev mode)" : "Reward paid out via MoMo 🎉");
      else toast.message(data.message || "Approved — payout pending", { duration: 6000 });
      reload();
    } catch (e) { handleErr(e); } finally { setBusy(null); }
  };
  const markPaid = async (o) => {
    const amt = window.prompt(`Confirm amount paid to ${o.name} (RWF):`, o.rewardAmountFrw || o.amountFrw || "");
    if (amt === null) return;
    setBusy(o.id);
    try { await endpoints.adminMarkRewardPaid(o.id, { amountFrw: parseInt(amt || "0", 10) }); toast.success("Marked as paid"); reload(); }
    catch (e) { handleErr(e); } finally { setBusy(null); }
  };
  const retry = async (o) => { setBusy(o.id); try { const { data } = await endpoints.adminRetryPayout(o.id); toast.message(`Payout ${data.payoutStatus}`); reload(); } catch (e) { handleErr(e); } finally { setBusy(null); } };
  const moderate = async (o, status) => { try { await endpoints.adminModerateOption(o.id, status); toast.success(`Contestant ${status}`); reload(); } catch (e) { handleErr(e); } };

  const payoutCell = (o) => {
    const st = o.payoutStatus || "pending";
    return (
      <div className="flex items-center gap-1.5 flex-wrap justify-end">
        <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded ${PAYOUT_BADGE[st] || PAYOUT_BADGE.pending}`} data-testid={`payout-status-${o.id}`}>{st}</span>
        {o.mtnRef && <span className="text-[10px] text-slate-500">ref {o.mtnRef}</span>}
        {(st === "pending" || st === "approved") && <Btn onClick={() => approve(o)} disabled={busy === o.id} data-testid={`approve-${o.id}`} className="h-8 px-3"><Banknote className="w-3.5 h-3.5" /> {busy === o.id ? "…" : "Approve & pay"}</Btn>}
        {st === "failed" && <Btn onClick={() => retry(o)} disabled={busy === o.id} data-testid={`retry-${o.id}`} className="h-8 px-3">Retry</Btn>}
        {st !== "paid" && <Btn variant="ghost" onClick={() => markPaid(o)} disabled={busy === o.id} className="h-8 px-3">Mark paid</Btn>}
      </div>
    );
  };

  return (
    <div className="border-t border-[#273042] p-4 bg-[#0A0C10]/40">
      <div className="grid sm:grid-cols-5 gap-2 items-end mb-4">
        <Field label="Name" placeholder="Participant / option" value={opt.name} onChange={(e) => setOpt({ ...opt, name: e.target.value })} data-testid={`opt-name-${c.id}`} />
        <Field label="Description" placeholder="Optional" value={opt.description} onChange={(e) => setOpt({ ...opt, description: e.target.value })} />
        <Field label="Image URL" placeholder="Optional" value={opt.imageUrl} onChange={(e) => setOpt({ ...opt, imageUrl: e.target.value })} />
        {c.isReward && <Field label="Payout phone" placeholder="+2507…" value={opt.payoutPhone} onChange={(e) => setOpt({ ...opt, payoutPhone: e.target.value })} data-testid={`opt-payout-${c.id}`} />}
        <Btn onClick={add} data-testid={`opt-add-${c.id}`}><Plus className="w-4 h-4" /> Add</Btn>
      </div>

      {(c.options || []).length === 0 ? <div className="text-slate-500 text-sm">No options yet.</div> : (
        <div className="space-y-2">
          {c.options.map((o) => (
            <div key={o.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-[#273042] bg-[#121620] p-3" data-testid={`admin-opt-${o.id}`}>
              <div className="w-7 h-7 rounded-full bg-[#273042] text-slate-200 flex items-center justify-center text-xs font-bold">{o.rank}</div>
              <div className="min-w-0 flex-1">
                <div className="text-white text-sm font-semibold truncate flex items-center gap-2">{o.name}
                  {o.approvalStatus && o.approvalStatus !== "approved" && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">{o.approvalStatus}</span>}
                </div>
                <div className="text-xs text-slate-400">{(o.votes ?? 0).toLocaleString()} votes · {o.percent}% · reward {(o.computedRewardFrw ?? o.amountFrw ?? 0).toLocaleString()} RWF{o.payoutPhone ? ` · ${o.payoutPhone}` : ""}</div>
              </div>
              {o.approvalStatus === "pending" ? (
                <div className="flex items-center gap-1.5">
                  <Btn onClick={() => moderate(o, "approved")} data-testid={`moderate-approve-${o.id}`} className="h-8 px-3">Approve</Btn>
                  <Btn variant="ghost" onClick={() => moderate(o, "rejected")} className="h-8 px-3">Reject</Btn>
                </div>
              ) : (
                <>
                  {c.isReward && payoutCell(o)}
                  {o.approvalStatus === "approved" && c.allowSignups && <Btn variant="ghost" onClick={() => moderate(o, "suspended")} className="h-8 px-3">Suspend</Btn>}
                </>
              )}
              <DeleteBtn onClick={() => del(o.id)} testid={`opt-del-${o.id}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminVoting() {
  const { data: campaigns, loading, reload } = useCrud(endpoints.adminVoteCampaigns);
  const [form, setForm] = useState(BLANK);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      await endpoints.adminCreateCampaign({ ...form, pointsPerVote: parseInt(form.pointsPerVote || 1, 10),
        startAt: toIso(form.startAt), closeAt: toIso(form.closeAt) });
      toast.success("Campaign created (draft). Add options, then Open it."); setForm(BLANK); setShowForm(false); reload();
    } catch (e) { handleErr(e); } finally { setSaving(false); }
  };
  const setStatus = async (c, status) => { try { await endpoints.adminUpdateCampaign(c.id, { status }); toast.success(status === "open" ? "Campaign opened" : "Campaign closed"); reload(); } catch (e) { handleErr(e); } };
  const close = async (c) => { if (!window.confirm("Close voting and lock the result?")) return; try { await endpoints.adminCloseCampaign(c.id); toast.success("Closed · winner locked"); reload(); } catch (e) { handleErr(e); } };
  const del = async (c) => { if (!window.confirm(`Delete "${c.title}" and all its votes?`)) return; try { await endpoints.adminDeleteCampaign(c.id); toast.success("Deleted"); reload(); } catch (e) { handleErr(e); } };
  const exportCsv = async (c) => {
    try { const { data } = await endpoints.adminVotesCsv(c.id); const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = `votes-${c.title}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { handleErr(e); }
  };

  return (
    <div>
      <PageHeader title="Voting" desc="Create free polls or paid points campaigns, manage participants and reward payouts.">
        <Btn onClick={() => setShowForm((s) => !s)} data-testid="new-campaign-toggle"><Plus className="w-4 h-4" /> New campaign</Btn>
      </PageHeader>

      {showForm && (
        <Card className="p-5 mb-6 space-y-3" data-testid="campaign-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="campaign-title" />
            <ImageUpload label="Cover image" value={form.coverUrl} onChange={(url) => setForm({ ...form, coverUrl: url })} testid="campaign-cover" />
          </div>
          <Field label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="grid sm:grid-cols-4 gap-3">
            <Select label="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <option value="free">Free (1 vote/member)</option><option value="paid">Paid (points)</option>
            </Select>
            {form.type === "paid" && <Field label="Points per vote" type="number" min={1} value={form.pointsPerVote} onChange={(e) => setForm({ ...form, pointsPerVote: e.target.value })} data-testid="campaign-ppv" />}
            <Field label="Opens (optional)" type="datetime-local" value={form.startAt} onChange={(e) => setForm({ ...form, startAt: e.target.value })} />
            <Field label="Closes (optional)" type="datetime-local" value={form.closeAt} onChange={(e) => setForm({ ...form, closeAt: e.target.value })} />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.isReward} onChange={(e) => setForm({ ...form, isReward: e.target.checked })} data-testid="campaign-reward" /> Reward campaign (payout to winner)</label>
            <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.resultsPublic} onChange={(e) => setForm({ ...form, resultsPublic: e.target.checked })} /> Show results publicly</label>
            <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.allowSignups} onChange={(e) => setForm({ ...form, allowSignups: e.target.checked })} data-testid="campaign-signups" /> Allow contestant self-registration</label>
          </div>
          <div className="flex gap-3"><Btn onClick={create} disabled={saving} data-testid="campaign-create">{saving ? "Creating…" : "Create campaign"}</Btn><Btn variant="ghost" onClick={() => { setShowForm(false); setForm(BLANK); }}>Cancel</Btn></div>
        </Card>
      )}

      {loading ? <Loading /> : campaigns.length === 0 ? <Empty text="No campaigns yet. Create your first voting campaign." /> : (
        <div className="space-y-4">
          {campaigns.map((c) => {
            const eff = c.effectiveStatus || c.status;
            return (
              <Card key={c.id} data-testid={`admin-campaign-${c.id}`}>
                <div className="p-4 flex flex-wrap items-center gap-3">
                  <button onClick={() => setExpanded(expanded === c.id ? null : c.id)} className="text-slate-400 hover:text-white">{expanded === c.id ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}</button>
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-bold flex items-center gap-2 truncate">{c.title}
                      {c.type === "paid" && <Coins className="w-4 h-4 text-[#F5B301]" />}
                      {c.isReward && <Trophy className="w-4 h-4 text-[#2563EB]" />}
                    </div>
                    <div className="text-xs text-slate-400">{eff} · {(c.totalVotes ?? 0).toLocaleString()} votes{c.type === "paid" ? ` · ${(c.totalAmountFrw ?? 0).toLocaleString()} RWF` : ""} · {(c.options || []).length} options</div>
                  </div>
                  <span className={`text-[10px] font-extrabold uppercase px-2 py-1 rounded ${eff === "open" ? "bg-emerald-500/15 text-emerald-400" : eff === "closed" ? "bg-slate-600/30 text-slate-300" : eff === "scheduled" ? "bg-amber-500/15 text-amber-400" : "bg-white/5 text-slate-400"}`}>{eff}</span>
                  {eff === "draft" || eff === "scheduled" ? <Btn onClick={() => setStatus(c, "open")} data-testid={`open-${c.id}`} className="h-9"><Play className="w-4 h-4" /> Open</Btn> : null}
                  {eff === "open" && <Btn variant="ghost" onClick={() => close(c)} data-testid={`close-${c.id}`} className="h-9"><Square className="w-4 h-4" /> Close</Btn>}
                  <Btn variant="ghost" onClick={() => exportCsv(c)} className="h-9"><Download className="w-4 h-4" /></Btn>
                  <DeleteBtn onClick={() => del(c)} testid={`campaign-del-${c.id}`} />
                </div>
                {expanded === c.id && <Options c={c} reload={reload} />}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
